// src/components/AdminPanel/SiteSettings.js
import React, { useState, useEffect, useRef } from "react";
import LoadingAnimation from "../LoadingAnimation";
import { toast } from "react-toastify";
import { db } from "../../firebase";
import { doc, getDoc, updateDoc, setDoc } from "firebase/firestore";

// ─── CLOUDINARY UPLOAD FUNCTION ────────────────────────────────────────────
// ─── CLOUDINARY UPLOAD FUNCTION ────────────────────────────────────────────
const uploadImage = async (file, folder) => {
    // Using your client's Cloudinary credentials
    const CLOUD_NAME = "djgfcak5i";
    const UPLOAD_PRESET = "front_upload";
    
    const url = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`;
    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", UPLOAD_PRESET);
    data.append("folder", folder);
    
    const res = await fetch(url, { method: "POST", body: data });
    const json = await res.json();
    return json.secure_url;
  };

// ─────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────

const SiteSettings = () => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState({
    headerLogo: false,
    footerLogo: false,
    sidebarLogo: false,
    emailLogo: false,
  });
  
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  
  // Logo states
  const [headerLogo, setHeaderLogo] = useState("");
  const [footerLogo, setFooterLogo] = useState("");
  const [sidebarLogo, setSidebarLogo] = useState("");
  const [emailLogo, setEmailLogo] = useState("");
  
  // Logo previews
  const [headerPreview, setHeaderPreview] = useState("");
  const [footerPreview, setFooterPreview] = useState("");
  const [sidebarPreview, setSidebarPreview] = useState("");
  const [emailPreview, setEmailPreview] = useState("");
  
  // Selected files
  const [headerFile, setHeaderFile] = useState(null);
  const [footerFile, setFooterFile] = useState(null);
  const [sidebarFile, setSidebarFile] = useState(null);
  const [emailFile, setEmailFile] = useState(null);
  
  // Contact info states
  const [contactInfo, setContactInfo] = useState({
    email: "",
    landline: "",
    mobile: "",
    officeTimings: "",
    address: "",
    useCustomAddress: false, // Flag for custom address
  });
  
  // Original contact info for cancel functionality
  const [originalContactInfo, setOriginalContactInfo] = useState({
    email: "",
    landline: "",
    mobile: "",
    officeTimings: "",
    address: "",
    useCustomAddress: false,
  });
  
  // Predefined address options
  const addressOptions = [
    "B-343, Pagganwala Street, Near Cheema Masjid, Shadman Colony, Gujrat, Pakistan",
  
  ];
  
  // Refs for file inputs
  const headerInputRef = useRef(null);
  const footerInputRef = useRef(null);
  const sidebarInputRef = useRef(null);
  const emailInputRef = useRef(null);
  
  // Fetch data on component mount
  useEffect(() => {
    fetchSiteData();
  }, []);
  
  const fetchSiteData = async () => {
    setLoading(true);
    try {
      // Fetch logos from Firestore
      const logosRef = doc(db, "logos", "xrSCDlcVzp10becG8bnW");
      const logosSnap = await getDoc(logosRef);
      
      if (logosSnap.exists()) {
        const data = logosSnap.data();
        setHeaderLogo(data.navbarLogo || data.headerLogo || "");
        setFooterLogo(data.footerLogo || "");
        setSidebarLogo(data.sidebarLogo || data.navbarLogo || "");
        setEmailLogo(data.emailLogo || "");
        setHeaderPreview(data.navbarLogo || data.headerLogo || "");
        setFooterPreview(data.footerLogo || "");
        setSidebarPreview(data.sidebarLogo || data.navbarLogo || "");
        setEmailPreview(data.emailLogo || "");
      }
      
      // Fetch contact info from Firestore
      const contactRef = doc(db, "contactInfo", "siteContact");
      const contactSnap = await getDoc(contactRef);
      
      if (contactSnap.exists()) {
        const data = contactSnap.data();
        const fetchedContact = {
          email: data.email || data.firstEmail || data["first email"] || "",
          landline: data.landline || data.phoneOne || data["phone one"] || "",
          mobile: data.mobile || data.phoneTwo || data["phone two"] || "",
          officeTimings: data.officeTimings || data["office timings"] || "",
          address: data.address || addressOptions[0],
          useCustomAddress: data.useCustomAddress || false,
        };
        setContactInfo(fetchedContact);
        setOriginalContactInfo(fetchedContact);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch site data.");
    } finally {
      setLoading(false);
    }
  };
  
  // Handle logo file selection
  const handleLogoChange = (type, e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file.");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB.");
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (ev) => {
      if (type === 'header') setHeaderPreview(ev.target.result);
      if (type === 'footer') setFooterPreview(ev.target.result);
      if (type === 'sidebar') setSidebarPreview(ev.target.result);
      if (type === 'email') setEmailPreview(ev.target.result);
    };
    reader.readAsDataURL(file);
    
    if (type === 'header') setHeaderFile(file);
    if (type === 'footer') setFooterFile(file);
    if (type === 'sidebar') setSidebarFile(file);
    if (type === 'email') setEmailFile(file);
    
    toast.info(`${type.charAt(0).toUpperCase() + type.slice(1)} logo selected. Click "Update Logos" to save.`);
  };
  
  // Upload logo to Cloudinary and save to Firestore
  const uploadLogo = async (file, type) => {
    if (!file) return null;
    
    setUploading(prev => ({ ...prev, [type]: true }));
    try {
      const folder = type === 'sidebarLogo' ? 'logos/sidebar' : type === 'emailLogo' ? 'logos/email' : 'logos';
      const imageUrl = await uploadImage(file, folder);
      return imageUrl;
    } catch (err) {
      console.error(err);
      toast.error(`Failed to upload ${type.replace('Logo', '')} logo.`);
      return null;
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };
  
  // Edit/Save logos
  const handleEditLogos = async () => {
    if (!headerFile && !footerFile && !sidebarFile && !emailFile) {
      toast.info("No logo changes to save. Please select a logo first.");
      return;
    }
    
    setLoading(true);
    const toastId = toast.loading("Updating logos...");
    
    try {
      const logosRef = doc(db, "logos", "xrSCDlcVzp10becG8bnW");
      const updates = {};
      let hasUpdates = false;
      
      // Upload header logo if changed
      if (headerFile) {
        const url = await uploadLogo(headerFile, 'headerLogo');
        if (url) {
          updates.navbarLogo = url;
          updates.headerLogo = url;
          setHeaderLogo(url);
          setHeaderPreview(url);
          hasUpdates = true;
        }
      }
      
      // Upload footer logo if changed
      if (footerFile) {
        const url = await uploadLogo(footerFile, 'footerLogo');
        if (url) {
          updates.footerLogo = url;
          setFooterLogo(url);
          setFooterPreview(url);
          hasUpdates = true;
        }
      }
      
      // Upload sidebar logo if changed
      if (sidebarFile) {
        const url = await uploadLogo(sidebarFile, 'sidebarLogo');
        if (url) {
          updates.sidebarLogo = url;
          setSidebarLogo(url);
          setSidebarPreview(url);
          hasUpdates = true;
        }
      }
      
      // Upload email logo if changed
      if (emailFile) {
        const url = await uploadLogo(emailFile, 'emailLogo');
        if (url) {
          updates.emailLogo = url;
          setEmailLogo(url);
          setEmailPreview(url);
          hasUpdates = true;
        }
      }
      
      if (hasUpdates) {
        await updateDoc(logosRef, updates);
        toast.update(toastId, { render: "Logos updated successfully!", type: "success", isLoading: false, autoClose: 3000 });
        
        // Clear file states
        setHeaderFile(null);
        setFooterFile(null);
        setSidebarFile(null);
        setEmailFile(null);
        if (headerInputRef.current) headerInputRef.current.value = "";
        if (footerInputRef.current) footerInputRef.current.value = "";
        if (sidebarInputRef.current) sidebarInputRef.current.value = "";
        if (emailInputRef.current) emailInputRef.current.value = "";
      } else {
        toast.update(toastId, { render: "No logos were updated.", type: "info", isLoading: false, autoClose: 3000 });
      }
    } catch (err) {
      console.error(err);
      toast.update(toastId, { render: "Failed to update logos.", type: "error", isLoading: false, autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle contact info change (Edit)
  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactInfo(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle custom address toggle
  const handleCustomAddressToggle = () => {
    setContactInfo(prev => ({ ...prev, useCustomAddress: !prev.useCustomAddress }));
  };
  
  // Enable edit mode
  const handleEditClick = () => {
    setIsEditing(true);
  };
  
  // Cancel edit
  const handleCancelEdit = () => {
    setContactInfo(originalContactInfo);
    setIsEditing(false);
  };
  
  // Edit/Save contact info
  const handleEditContactInfo = async () => {
    setLoading(true);
    const toastId = toast.loading("Updating contact information...");
    
    try {
      const contactRef = doc(db, "contactInfo", "siteContact");
      
      const contactData = {
        email: contactInfo.email,
        landline: contactInfo.landline,
        mobile: contactInfo.mobile,
        officeTimings: contactInfo.officeTimings,
        address: contactInfo.address,
        useCustomAddress: contactInfo.useCustomAddress,
        updatedAt: new Date(),
      };
      
      await setDoc(contactRef, contactData, { merge: true });
      
      // Update original contact info
      setOriginalContactInfo(contactInfo);
      
      toast.update(toastId, { render: "Contact information updated successfully!", type: "success", isLoading: false, autoClose: 3000 });
      
      // Exit edit mode
      setIsEditing(false);
    } catch (err) {
      console.error(err);
      toast.update(toastId, { render: "Failed to update contact information.", type: "error", isLoading: false, autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };
  
  if (loading && !headerLogo && !footerLogo && !sidebarLogo && !emailLogo && !contactInfo.email) {
    return <LoadingAnimation />;
  }
  
  return (
    <div className="postjob-container">
      <h2 className="page-heading">
        <i className="fas fa-cog" style={{ color: "#FF8000" }}></i>
        Site Settings
      </h2>
      
      {/* Logo Management Section */}
      <div style={{ marginBottom: "40px" }}>
        <h3 style={{ borderBottom: "2px solid #FF8000", paddingBottom: "10px", marginBottom: "20px" }}>
          <i className="fas fa-image" style={{ color: "#FF8000", marginRight: "10px" }}></i>
          Logo Management
        </h3>
        
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "24px" }}>
          
          {/* Header Logo */}
          <div style={{ border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", backgroundColor: "#f9fafb" }}>
            <h4 style={{ margin: "0 0 15px 0", color: "#2D3748" }}>Header Logo</h4>
            <div style={{ textAlign: "center", marginBottom: "15px" }}>
              {headerPreview ? (
                <img src={headerPreview} alt="Header Logo Preview" style={{ maxHeight: "60px", maxWidth: "100%", objectFit: "contain" }} />
              ) : (
                <div style={{ padding: "20px", backgroundColor: "#e2e8f0", borderRadius: "8px", color: "#718096" }}>No logo uploaded</div>
              )}
            </div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                width: "100%",
                padding: "10px",
                border: "1px solid #FF8000",
                borderRadius: "8px",
                backgroundColor: "#fff",
                cursor: "pointer",
                color: "#FF8000",
                fontWeight: "500",
              }}
            >
              <input ref={headerInputRef} type="file" accept="image/*" onChange={(e) => handleLogoChange('header', e)} style={{ display: "none" }} />
              <i className="fas fa-edit"></i> Edit Header Logo
            </label>
            {headerFile && (
              <p style={{ fontSize: "12px", color: "#22c55e", marginTop: "8px", textAlign: "center" }}>
                <i className="fas fa-check-circle"></i> New logo selected
              </p>
            )}
          </div>
          
          {/* Footer Logo */}
          <div style={{ border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", backgroundColor: "#f9fafb" }}>
            <h4 style={{ margin: "0 0 15px 0", color: "#2D3748" }}>Footer Logo</h4>
            <div style={{ textAlign: "center", marginBottom: "15px" }}>
              {footerPreview ? (
                <img src={footerPreview} alt="Footer Logo Preview" style={{ maxHeight: "60px", maxWidth: "100%", objectFit: "contain" }} />
              ) : (
                <div style={{ padding: "20px", backgroundColor: "#e2e8f0", borderRadius: "8px", color: "#718096" }}>No logo uploaded</div>
              )}
            </div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                width: "100%",
                padding: "10px",
                border: "1px solid #FF8000",
                borderRadius: "8px",
                backgroundColor: "#fff",
                cursor: "pointer",
                color: "#FF8000",
                fontWeight: "500",
              }}
            >
              <input ref={footerInputRef} type="file" accept="image/*" onChange={(e) => handleLogoChange('footer', e)} style={{ display: "none" }} />
              <i className="fas fa-edit"></i> Edit Footer Logo
            </label>
            {footerFile && (
              <p style={{ fontSize: "12px", color: "#22c55e", marginTop: "8px", textAlign: "center" }}>
                <i className="fas fa-check-circle"></i> New logo selected
              </p>
            )}
          </div>
          
          {/* Sidebar Logo */}
          <div style={{ border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", backgroundColor: "#f9fafb" }}>
            <h4 style={{ margin: "0 0 15px 0", color: "#2D3748" }}>Sidebar Logo</h4>
            <div style={{ textAlign: "center", marginBottom: "15px" }}>
              {sidebarPreview ? (
                <img src={sidebarPreview} alt="Sidebar Logo Preview" style={{ maxHeight: "60px", maxWidth: "100%", objectFit: "contain" }} />
              ) : (
                <div style={{ padding: "20px", backgroundColor: "#e2e8f0", borderRadius: "8px", color: "#718096" }}>No logo uploaded</div>
              )}
            </div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                width: "100%",
                padding: "10px",
                border: "1px solid #FF8000",
                borderRadius: "8px",
                backgroundColor: "#fff",
                cursor: "pointer",
                color: "#FF8000",
                fontWeight: "500",
              }}
            >
              <input ref={sidebarInputRef} type="file" accept="image/*" onChange={(e) => handleLogoChange('sidebar', e)} style={{ display: "none" }} />
              <i className="fas fa-edit"></i> Edit Sidebar Logo
            </label>
            {sidebarFile && (
              <p style={{ fontSize: "12px", color: "#22c55e", marginTop: "8px", textAlign: "center" }}>
                <i className="fas fa-check-circle"></i> New logo selected
              </p>
            )}
          </div>
          
          {/* Email Template Logo */}
          <div style={{ border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", backgroundColor: "#f9fafb" }}>
            <h4 style={{ margin: "0 0 15px 0", color: "#2D3748" }}>Email Template Logo</h4>
            <div style={{ textAlign: "center", marginBottom: "15px" }}>
              {emailPreview ? (
                <img src={emailPreview} alt="Email Logo Preview" style={{ maxHeight: "60px", maxWidth: "100%", objectFit: "contain" }} />
              ) : (
                <div style={{ padding: "20px", backgroundColor: "#e2e8f0", borderRadius: "8px", color: "#718096" }}>No logo uploaded</div>
              )}
            </div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                width: "100%",
                padding: "10px",
                border: "1px solid #FF8000",
                borderRadius: "8px",
                backgroundColor: "#fff",
                cursor: "pointer",
                color: "#FF8000",
                fontWeight: "500",
              }}
            >
              <input ref={emailInputRef} type="file" accept="image/*" onChange={(e) => handleLogoChange('email', e)} style={{ display: "none" }} />
              <i className="fas fa-envelope"></i> Edit Email Logo
            </label>
            {emailFile && (
              <p style={{ fontSize: "12px", color: "#22c55e", marginTop: "8px", textAlign: "center" }}>
                <i className="fas fa-check-circle"></i> New logo selected
              </p>
            )}
          </div>
        </div>
        
        <div style={{ marginTop: "20px", textAlign: "right" }}>
          <button
            onClick={handleEditLogos}
            disabled={loading || uploading.headerLogo || uploading.footerLogo || uploading.sidebarLogo || uploading.emailLogo}
            style={{
              backgroundColor: "#FF8000",
              color: "white",
              padding: "12px 24px",
              border: "none",
              borderRadius: "8px",
              cursor: (loading || uploading.headerLogo || uploading.footerLogo || uploading.sidebarLogo || uploading.emailLogo) ? "not-allowed" : "pointer",
              fontSize: "14px",
              fontWeight: "600",
              opacity: (loading || uploading.headerLogo || uploading.footerLogo || uploading.sidebarLogo || uploading.emailLogo) ? 0.7 : 1,
            }}
          >
            {uploading.headerLogo || uploading.footerLogo || uploading.sidebarLogo || uploading.emailLogo ? "Uploading..." : "Update Logos"}
          </button>
        </div>
      </div>
      
      {/* Contact Information Section - Responsive */}
      <div>
        <h3 style={{ borderBottom: "2px solid #FF8000", paddingBottom: "10px", marginBottom: "20px" }}>
          <i className="fas fa-address-card" style={{ color: "#FF8000", marginRight: "10px" }}></i>
          Contact Information
        </h3>
        
        {!isEditing ? (
          // Display Mode
          <div>
            <div style={{ 
              backgroundColor: "#f9fafb", 
              border: "1px solid #e2e8f0", 
              borderRadius: "12px", 
              padding: "24px",
              marginBottom: "20px",
              overflowX: "auto"
            }}>
              <div style={{ 
                display: "grid", 
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", 
                gap: "16px"
              }}>
                <div>
                  <strong style={{ color: "#4a5568", display: "block", marginBottom: "4px" }}>Email:</strong>
                  <span style={{ color: "#2D3748", wordBreak: "break-word" }}>{contactInfo.email || "Not set"}</span>
                </div>
                <div>
                  <strong style={{ color: "#4a5568", display: "block", marginBottom: "4px" }}>Landline:</strong>
                  <span style={{ color: "#2D3748", wordBreak: "break-word" }}>{contactInfo.landline || "Not set"}</span>
                </div>
                <div>
                  <strong style={{ color: "#4a5568", display: "block", marginBottom: "4px" }}>Mobile:</strong>
                  <span style={{ color: "#2D3748", wordBreak: "break-word" }}>{contactInfo.mobile || "Not set"}</span>
                </div>
                <div>
                  <strong style={{ color: "#4a5568", display: "block", marginBottom: "4px" }}>Office Timings:</strong>
                  <span style={{ color: "#2D3748", wordBreak: "break-word" }}>{contactInfo.officeTimings || "Not set"}</span>
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <strong style={{ color: "#4a5568", display: "block", marginBottom: "4px" }}>Address:</strong>
                  <span style={{ color: "#2D3748", wordBreak: "break-word" }}>{contactInfo.address || "Not set"}</span>
                </div>
              </div>
            </div>
            
            <div style={{ textAlign: "right" }}>
              <button
                onClick={handleEditClick}
                style={{
                  backgroundColor: "#FF8000",
                  color: "white",
                  padding: "12px 24px",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                <i className="fas fa-edit" style={{ marginRight: "8px" }}></i>
                Edit Contact Information
              </button>
            </div>
          </div>
        ) : (
          // Edit Mode - Responsive
          <div>
            <div style={{ 
              display: "flex", 
              flexDirection: "column", 
              gap: "20px"
            }}>
              <div style={{ 
                display: "grid", 
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", 
                gap: "20px"
              }}>
                <div>
                  <label style={{ display: "block", marginBottom: "8px", fontWeight: "600", color: "#4a5568" }}>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={contactInfo.email}
                    onChange={handleContactChange}
                    placeholder="info@visionbird.com"
                    style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "14px", boxSizing: "border-box" }}
                  />
                </div>
                
                <div>
                  <label style={{ display: "block", marginBottom: "8px", fontWeight: "600", color: "#4a5568" }}>Landline</label>
                  <input
                    type="text"
                    name="landline"
                    value={contactInfo.landline}
                    onChange={handleContactChange}
                    placeholder="053-3728469"
                    style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "14px", boxSizing: "border-box" }}
                  />
                </div>
                
                <div>
                  <label style={{ display: "block", marginBottom: "8px", fontWeight: "600", color: "#4a5568" }}>Mobile</label>
                  <input
                    type="text"
                    name="mobile"
                    value={contactInfo.mobile}
                    onChange={handleContactChange}
                    placeholder="+92 301 6239096"
                    style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "14px", boxSizing: "border-box" }}
                  />
                </div>
                
                <div>
                  <label style={{ display: "block", marginBottom: "8px", fontWeight: "600", color: "#4a5568" }}>Office Timings</label>
                  <input
                    type="text"
                    name="officeTimings"
                    value={contactInfo.officeTimings}
                    onChange={handleContactChange}
                    placeholder="Monday - Friday, 9:00 AM - 6:00 PM"
                    style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "14px", boxSizing: "border-box" }}
                  />
                </div>
              </div>
              
              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "600", color: "#4a5568" }}>
                  Address
                  <span style={{ display: "flex", alignItems: "center", fontSize: "12px", fontWeight: "normal", color: "#718096", marginLeft: "10px" }}>
                    <input
                      type="checkbox"
                      checked={contactInfo.useCustomAddress}
                      onChange={handleCustomAddressToggle}
                      style={{ marginRight: "5px" }}
                    />
                    Use custom address
                  </span>
                </label>
                
                {!contactInfo.useCustomAddress ? (
                  <select
                    name="address"
                    value={contactInfo.address}
                    onChange={handleContactChange}
                    style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "14px", boxSizing: "border-box" }}
                  >
                    {addressOptions.map((option, index) => (
                      <option key={index} value={option}>{option}</option>
                    ))}
                  </select>
                ) : (
                  <textarea
                    name="address"
                    value={contactInfo.address}
                    onChange={handleContactChange}
                    placeholder="Enter custom address here..."
                    rows="3"
                    style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "14px", resize: "vertical", boxSizing: "border-box" }}
                  />
                )}
              </div>
            </div>
            
            <div style={{ marginTop: "20px", textAlign: "right", display: "flex", gap: "12px", justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button
                onClick={handleCancelEdit}
                disabled={loading}
                style={{
                  backgroundColor: "#e2e8f0",
                  color: "#4a5568",
                  padding: "12px 24px",
                  border: "none",
                  borderRadius: "8px",
                  cursor: loading ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleEditContactInfo}
                disabled={loading}
                style={{
                  backgroundColor: "#FF8000",
                  color: "white",
                  padding: "12px 24px",
                  border: "none",
                  borderRadius: "8px",
                  cursor: loading ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SiteSettings;